
import React, { useState } from 'react';
import { Check, X, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';
import { calculateMarginFromMarkup, calculateMarkupFromMargin } from '@/utils/pricing-utils';
import { formatCurrency } from '@/lib/utils';

interface MarkupTiersTableProps {
  tiers: LineItemMarkupTier[];
  onRemoveTier: (index: number) => void;
  onUpdateTier?: (index: number, updatedTier: LineItemMarkupTier) => void;
  editable?: boolean;
}

const MarkupTiersTable: React.FC<MarkupTiersTableProps> = ({ 
  tiers, 
  onRemoveTier,
  onUpdateTier,
  editable = true
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTier, setEditingTier] = useState<LineItemMarkupTier | null>(null);

  if (tiers.length === 0) {
    return (
      <p className="text-center py-4 text-sm text-muted-foreground">
        No custom tiers defined yet. Add your first tier below.
      </p>
    );
  }

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditingTier({ ...tiers[index] });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingTier(null);
  };

  const saveEdit = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    
    if (editingIndex !== null && editingTier && onUpdateTier) {
      onUpdateTier(editingIndex, editingTier);
    }
    setEditingIndex(null);
    setEditingTier(null);
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    cancelEdit();
  };

  const handleEditClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    startEdit(index);
  };

  const handleMarkupChange = (value: string) => {
    if (!editingTier) return;
    if (value === '') { setEditingTier({ ...editingTier, markup_percentage: '' as any, margin_percentage: 0 }); return; }
    const markupValue = parseFloat(value) || 0;
    setEditingTier({
      ...editingTier,
      markup_percentage: markupValue,
      margin_percentage: calculateMarginFromMarkup(markupValue)
    });
  };

  const handleMarginChange = (value: string) => {
    if (!editingTier) return;
    if (value === '') { setEditingTier({ ...editingTier, margin_percentage: '' as any, markup_percentage: 0 }); return; }
    const marginValue = parseFloat(value) || 0;
    setEditingTier({
      ...editingTier,
      margin_percentage: marginValue,
      markup_percentage: calculateMarkupFromMargin(marginValue)
    });
  };

  const handleMinAmountChange = (value: string) => {
    if (!editingTier) return;
    const amount = parseFloat(value);
    setEditingTier({
      ...editingTier,
      min_amount: isNaN(amount) ? 0 : amount
    });
  };

  const handleMaxAmountChange = (value: string) => {
    if (!editingTier) return;
    const amount = parseFloat(value);
    setEditingTier({
      ...editingTier,
      max_amount: value === '' || isNaN(amount) ? null : amount
    });
  };

  return (
    <div className="border rounded-md mb-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Min Amount</TableHead>
            <TableHead>Max Amount</TableHead>
            <TableHead>Markup %</TableHead>
            <TableHead>Margin %</TableHead>
            <TableHead className="w-[120px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tiers.map((tier, index) => (
            <TableRow key={tier.id || index}>
              <TableCell>
                {editingIndex === index ? (
                  <Input
                    type="number"
                    value={editingTier?.min_amount || 0}
                    onChange={(e) => handleMinAmountChange(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                ) : (
                  formatCurrency(tier.min_amount)
                )}
              </TableCell>
              <TableCell>
                {editingIndex === index ? (
                  <Input
                    type="number"
                    value={editingTier?.max_amount || ''}
                    onChange={(e) => handleMaxAmountChange(e.target.value)}
                    step="0.01"
                    min="0"
                    placeholder="No limit"
                  />
                ) : (
                  tier.max_amount !== null ? 
                    formatCurrency(tier.max_amount) : 
                    'No limit'
                )}
              </TableCell>
              <TableCell>
                {editingIndex === index ? (
                  <Input
                    type="number"
                    value={editingTier?.markup_percentage || 0}
                    onChange={(e) => handleMarkupChange(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                ) : (
                  `${tier.markup_percentage.toFixed(2)}%`
                )}
              </TableCell>
              <TableCell>
                {editingIndex === index ? (
                  <Input
                    type="number"
                    value={editingTier?.margin_percentage || 0}
                    onChange={(e) => handleMarginChange(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                ) : (
                  `${tier.margin_percentage.toFixed(2)}%`
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  {editingIndex === index ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={saveEdit}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </>
                  ) : (
                    <>
                      {editable && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleEditClick(e, index)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Markup Tier</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this markup tier? This action cannot be undone.
                              <br />
                              <br />
                              <strong>Tier:</strong> {formatCurrency(tier.min_amount)} - {tier.max_amount !== null ? formatCurrency(tier.max_amount) : 'No limit'}
                              <br />
                              <strong>Markup:</strong> {tier.markup_percentage.toFixed(2)}%
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => onRemoveTier(index)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default MarkupTiersTable;
